import React from 'react'
import { Card, Row, Col, Spinner, ProgressBar } from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { ContractMetrics } from '../types'
import * as S from '../styles'

interface ContractMetricsCardProps {
  data?: ContractMetrics
  loading: boolean
}

// total = 0 (empresa sem dados) dava NaN% / R$ NaN na tela
const percentOf = (part: number, total: number) =>
  total > 0 ? (part / total) * 100 : 0

export const ContractMetricsCard: React.FC<ContractMetricsCardProps> = ({
  data,
  loading
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  if (loading || !data) {
    return (
      <Card className="h-100">
        <Card.Header>
          <Card.Title as="h5">
            <i className="feather icon-file-text text-primary mr-2" />
            Métricas de Contratos
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <S.LoadingSpinner>
            <Spinner animation="border" />
          </S.LoadingSpinner>
        </Card.Body>
      </Card>
    )
  }

  // Chart data for contract status distribution
  const chartOptions = {
    chart: {
      type: 'donut' as const,
      height: 200
    },
    labels: ['Assinatura Pendente', 'Registrado na Blockchain'],
    colors: ['#ffc107', '#28a745'],
    legend: {
      position: 'bottom' as const,
      fontSize: '12px'
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return Math.round(val) + '%'
      }
    }
  }

  const chartSeries = [
    data.contractsByStatus.signatures_pending || 0,
    data.contractsByStatus.registered || 0
  ]

  const blockchainPercentage = percentOf(
    data.contractsRegisteredOnBlockchain,
    data.totalContracts
  )
  const tokenizationPercentage = percentOf(
    data.contractsWithTokens,
    data.totalContracts
  )

  return (
    <Card className="h-100">
      <Card.Header>
        <Card.Title as="h5">
          <i className="feather icon-file-text text-primary mr-2" />
          Métricas de Contratos
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Row className="mb-4">
          <Col sm={6}>
            <div className="text-center">
              <h3 className="text-primary mb-1">
                {formatNumber(data.totalContracts)}
              </h3>
              <p className="text-muted mb-0">Total de Contratos</p>
            </div>
          </Col>
          <Col sm={6}>
            <div className="text-center">
              <h3 className="text-success mb-1">
                {formatCurrency(data.averageContractValue)}
              </h3>
              <p className="text-muted mb-0">Valor Médio</p>
            </div>
          </Col>
        </Row>

        <div className="mb-4">
          <h6 className="mb-3">Distribuição por Status:</h6>
          <S.ChartContainer>
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="donut"
              height={200}
            />
          </S.ChartContainer>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Registrados na Blockchain</span>
            <span className="font-weight-bold">
              {data.contractsRegisteredOnBlockchain}/{data.totalContracts}
            </span>
          </div>
          <ProgressBar
            now={blockchainPercentage}
            variant="success"
            style={{ height: '6px' }}
          />
          <small className="text-muted">
            {blockchainPercentage.toFixed(1)}% registrados
          </small>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Contratos Tokenizados</span>
            <span className="font-weight-bold">
              {data.contractsWithTokens}/{data.totalContracts}
            </span>
          </div>
          <ProgressBar
            now={tokenizationPercentage}
            variant="info"
            style={{ height: '6px' }}
          />
          <small className="text-muted">
            {tokenizationPercentage.toFixed(1)}% tokenizados
          </small>
        </div>

        <div className="mt-4 pt-3 border-top">
          <Row>
            <Col sm={6}>
              <small className="text-muted d-block">Valor Total</small>
              <strong>{formatCurrency(data.totalContractValue)}</strong>
            </Col>
            <Col sm={6}>
              <small className="text-muted d-block">Criações Mensais</small>
              <strong>
                {data.monthlyContractCreation[
                  data.monthlyContractCreation.length - 1
                ]?.count || 0}
              </strong>
            </Col>
          </Row>
        </div>
      </Card.Body>
    </Card>
  )
}
